/**
 * Delivery-state reference data for the Outbox status filter + tag, ported from
 * the Ember `state-label-map.ts` + metrics measure gate.
 */
import type { ComponentProps } from 'react';
import type { StatusIndicator } from '@customerio/pluma-components/react';
import { capitalize } from '@/utils/string';

/** Never shown in the status filter: `holdout` is a hidden bucket, `viewed` isn't searchable. */
export const UNSEARCHABLE_DELIVERY_STATES: readonly string[] = ['holdout', 'viewed'];

/** State → label overrides (Ember `state-label-map.ts`); every other state capitalizes. */
const STATE_LABELS: Record<string, string> = {
  topic_unsubscribed: 'Topic unsubscribes',
  human_clicked: 'Human clicked',
  machine_clicked: 'Machine clicked',
  human_opened: 'Human opened',
  prefetch_opened: 'Machine opened',
};

export function stateLabel(state: string): string {
  return STATE_LABELS[state] ?? capitalize(state);
}

/** Status-tag color — derived from Pluma's `StatusIndicator` (the dot `Label` renders). */
export type DeliveryStateColor = NonNullable<ComponentProps<typeof StatusIndicator>['color']>;

/**
 * State → status-tag color, ported from the `labelColor` field of the Ember
 * `state-label-map.ts`. Unknown states fall back to grey (see `stateColor`).
 */
const STATE_COLORS: Record<string, DeliveryStateColor> = {
  unsubscribed: 'red',
  topic_unsubscribed: 'red',
  spammed: 'red',
  failed: 'red',
  undeliverable: 'red',
  converted: 'green',
  clicked: 'green',
  replied: 'green',
  human_clicked: 'green',
  machine_clicked: 'green',
  opened: 'green',
  human_opened: 'green',
  prefetch_opened: 'green',
  sent: 'green',
  delivered: 'green',
  suppressed: 'grey',
  bounced: 'grey',
  deferred: 'grey',
  queued: 'grey',
  holdout: 'grey',
  canceled: 'grey',
  drafted: 'yellow',
  attempted: 'yellow',
};

export function stateColor(state: string): DeliveryStateColor {
  return STATE_COLORS[state] ?? 'grey';
}

/**
 * Per-channel `delivered` descriptions; channels without a specific entry use
 * `DELIVERED_DEFAULT_DESCRIPTION` (other states use `STATE_DESCRIPTIONS`). Both
 * SMS channels (`twilio` + `sms`) share the phone-carrier copy.
 *
 * NOTE: Ember's `<delivery-state-tag>` fell back to the *email-ESP* copy for
 * any channel missing from this map (`description[actionType] ?? email_action`),
 * so an sms/line/whatsapp `delivered` row rendered "…email service provider
 * (ESP)". That's a latent bug — here the fallback is a channel-agnostic string
 * instead, so no non-email channel shows email wording.
 */
const SMS_DELIVERED_DESCRIPTION =
  "We received confirmation from Twilio that the message was sent to the recipient's phone carrier.";
const DELIVERED_DEFAULT_DESCRIPTION = 'We received confirmation that the message was delivered.';
const DELIVERED_DESCRIPTIONS: Record<string, string> = {
  push: 'We received confirmation from the device that the message was received.',
  twilio: SMS_DELIVERED_DESCRIPTION,
  sms: SMS_DELIVERED_DESCRIPTION,
  email:
    "We received confirmation from the delivery provider that an email was sent to the recipient's email service provider (ESP).",
};

/**
 * State → tooltip description, ported from `state-label-map.ts`. `delivered` is
 * resolved per channel by `stateDescription`.
 */
const STATE_DESCRIPTIONS: Record<string, string> = {
  unsubscribed: 'Recipient unsubscribed from future messages.',
  topic_unsubscribed: 'Recipient unsubscribed from the message topic.',
  converted: 'Recipient completed the goal for the campaign.',
  spammed: 'Recipient marked message as spam.',
  clicked: 'Recipient clicked on a tracked link in the message.',
  replied: 'Recipient replied to the message.',
  human_clicked: 'Human clicked on a tracked link in the message.',
  machine_clicked: 'Machine clicked on a tracked link in the message.',
  opened: 'Recipient opened the message.',
  human_opened: 'Human opened the message.',
  prefetch_opened: 'Machine opened the message.',
  suppressed: 'Not delivered because a previous message to recipient bounced or was marked as spam.',
  bounced: "Not delivered because recipient's address is invalid or temporarily unavailable.",
  sent: 'Successfully sent to delivery provider.',
  failed: 'This message failed to reach the delivery provider and was not sent.',
  undeliverable: 'We did not attempt to send this message. Expand the delivery to see what prevented sending.',
  deferred: 'This message is deferred to be sent later.',
  drafted: 'Ready to be sent with your approval.',
  attempted: "We've attempted to send this but weren't successful. Retrying…",
  queued: 'This message has been queued and will be sent soon.',
  holdout: "This message is a holdout test and won't be delivered to any recipients.",
  canceled: 'The newsletter was canceled before this message could be sent.',
};

export function stateDescription(state: string, channel?: string): string | undefined {
  if (state === 'delivered') {
    return (channel != null ? DELIVERED_DESCRIPTIONS[channel] : undefined) ?? DELIVERED_DEFAULT_DESCRIPTION;
  }
  return STATE_DESCRIPTIONS[state];
}

/**
 * Port of `isDeliveryMeasureEnabledForChannel`: in the full measure map every
 * `disabledForChannels` set is empty except `opened` (disabled for twilio/sms),
 * `replied` (enabled only for twilio/sms), and the conversion/unsubscribe
 * measures (disabled for live_notification). The `human_`/`prefetch_` prefixes
 * are stripped first.
 */
export function isDeliveryStateEnabledForChannel(state: string, channel: string): boolean {
  const base = state.replace(/^human_/, '').replace(/^prefetch_/, '');
  if (base === 'opened') return channel !== 'twilio' && channel !== 'sms';
  if (base === 'replied') return channel === 'twilio' || channel === 'sms';
  if (base === 'converted' || base === 'unsubscribed' || base === 'topic_unsubscribed') {
    return channel !== 'live_notification';
  }
  return true;
}
